/**
 * @description simple component for displaying a no results message
 */

import React from 'react';
import { PropTypes } from 'prop-types';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import RemoveCircleOutlineOutlined from '@mui/icons-material/RemoveCircleOutlineOutlined';
import Box from '@mui/material/Box';
import styled from '@mui/system/styled';

const commonStyles = (props) => ({
  color: props.theme.palette.text.secondary,
  // Default to 14px
  fontSize: props.sx?.fontSize ?? '0.875em',
  fontWeight: props.theme.typography.fontWeightBold
})

const HorizontalContainer = styled(Box)((props) => ({
  ...commonStyles(props),
  display: 'flex',
  gap: '1rem',
  justifyContent: 'center',
  alignItems: 'center'
}));

const VerticalContainer = styled(Stack)((props) => ({
  ...commonStyles(props),
  alignItems: 'center',
  display: 'flex',
  width: '100%'
}));

const OverlayContainer = styled(Stack)((props) => ({
  ...commonStyles(props),
  alignItems: 'center',
  display: 'flex',
  height: '100%',
  justifyContent: 'center',
  width: '100%'
}));

const styles = {
  icon: {
    fontSize: '3em'
  },
  inlineIcon: {
    fontSize: '1.5em'
  },
  caption: {
    fontSize: 'inherit',
    fontWeight: 'bold'
  }
};

/**
 * @prop {string | element | elements[]} children - label for the no results banner
 * @prop {elementType} Icon - icon component that appears above the no results label (defaults to RemoveCircleOutlineOutlined icon)
 * @prop {object} sx - styling to be applied to the results container
 * @prop {string} variant - indicates if the no results should be treated as a "banner" or as an "overlay"
 */
const NoResults = ({
  children,
  Icon,
  sx,
  variant
}) => {
  if (variant === 'inline') {
    return (
      <HorizontalContainer sx={sx}>
        <Icon sx={styles.inlineIcon} />
        <Typography sx={styles.caption}>{children}</Typography>
      </HorizontalContainer>
    );
  } else if (variant === 'overlay') {
    return (
      <OverlayContainer sx={sx} spacing={1}>
        <Icon sx={styles.icon} />
        <Typography sx={styles.caption}>{children}</Typography>
      </OverlayContainer>
    );
  }

  return (
    <VerticalContainer sx={sx} spacing={1}>
      <Icon sx={styles.icon} />
      <Typography sx={styles.caption}>{children}</Typography>
    </VerticalContainer>
  );
};

NoResults.propTypes = {
  children: PropTypes.oneOfType([PropTypes.string, PropTypes.element, PropTypes.arrayOf(PropTypes.element)]).isRequired,
  Icon: PropTypes.elementType,
  sx: PropTypes.object,
  variant: PropTypes.string
};

NoResults.defaultProps = {
  Icon: RemoveCircleOutlineOutlined,
  sx: null,
  variant: 'banner'
};

export default NoResults;
